import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, Search, Key, Upload, Lock, Trash2 } from "lucide-react";
import { useKeyPairs } from "../context/KeyPairsContext";
import { useEntitlements } from "../context/EntitlementsContext";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * FontPicker — dropdown specialized for font family selection.
 * Iter33 user request: "Let's add a search feature to fonts, and there
 * should be a recently used fonts list showing up to 10. Let's also add
 * a section above that titled 'Key-Pair Fonts' that will show all fonts
 * assigned to key-pairs."
 *
 * Sections (in order):
 *   1. Key-Pair Fonts — fonts where any KeyPair has value_type='font'
 *      and the value is a valid CSS font name.
 *   2. Recently Used — last 10 fonts the user picked, persisted to
 *      localStorage at `cresc-doc-recent-fonts`.
 *   3. All Fonts — the canonical list, filterable via the search box.
 *
 * Renders dropdown via React portal (matches ThemedSelect) so parent
 * overflow:auto containers don't clip it.
 */
const ALL_FONTS = [
  "Inter", "Helvetica", "Arial", "Verdana", "Tahoma",
  "Georgia", "Garamond", "Times New Roman", "Palatino",
  "Trebuchet MS", "Lucida Sans", "Comic Sans MS", "Impact",
  "Menlo", "Monaco", "Courier New", "Consolas",
  "JetBrains Mono", "Fira Code", "Source Code Pro",
  "Roboto", "Open Sans", "Lato", "Montserrat", "Poppins",
  "Playfair Display", "Merriweather", "Bebas Neue", "Oswald",
];

const RECENT_KEY = "cresc-doc-recent-fonts";
const MAX_RECENTS = 10;

function loadRecents() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
}
function pushRecent(font) {
  if (!font) return;
  try {
    const cur = loadRecents();
    const next = [font, ...cur.filter((f) => f.toLowerCase() !== font.toLowerCase())].slice(0, MAX_RECENTS);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {}
}

export function FontPicker({ value, onChange, "data-testid": tid = "docs-fontname" }) {
  const { pairs } = useKeyPairs();
  const ent = useEntitlements();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recents, setRecents] = useState(loadRecents());
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0, width: 200 });
  const [customFonts, setCustomFonts] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Iter35 user request: "We need a custom font upload button, let's
  // make sure it's restricted to the proper price tier." Pro+ only.
  const canUploadCustomFonts = ent?.is_dev || ["pro", "business"].includes(ent?.plan);

  const loadCustomFonts = useCallback(async () => {
    try {
      const { data } = await api.get("/me/fonts");
      setCustomFonts(Array.isArray(data) ? data : []);
      // Inject @font-face rules for each custom font so CSS can use them.
      const styleId = "cresc-custom-fonts";
      let style = document.getElementById(styleId);
      if (!style) {
        style = document.createElement("style");
        style.id = styleId;
        document.head.appendChild(style);
      }
      style.textContent = (data || []).map((f) =>
        `@font-face { font-family: "${f.family.replace(/"/g, "")}"; src: url(${f.data_url}); }`
      ).join("\n");
    } catch { /* unauth or 402 */ }
  }, []);

  useEffect(() => { loadCustomFonts(); }, [loadCustomFonts]);

  const onUploadFile = async (file) => {
    if (!file) return;
    if (!canUploadCustomFonts) {
      window.dispatchEvent(new CustomEvent("crescendo:paywall", {
        detail: { error: "feature_locked", feature: "custom_fonts", plan: "pro", message: "Custom font uploads require Pro." },
      }));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Font file must be under 2MB");
      return;
    }
    if (!/\.(woff2?|ttf|otf)$/i.test(file.name)) {
      toast.error("Use .woff, .woff2, .ttf, or .otf");
      return;
    }
    const family = file.name.replace(/\.(woff2?|ttf|otf)$/i, "");
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target.result;
        try {
          await api.post("/me/fonts", { family, data_url: dataUrl });
          await loadCustomFonts();
          toast.success(`Uploaded "${family}"`);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          if (typeof detail === "object" && detail?.error === "feature_locked") {
            window.dispatchEvent(new CustomEvent("crescendo:paywall", { detail }));
          } else {
            toast.error(typeof detail === "string" ? detail : "Upload failed");
          }
        }
        setUploading(false);
      };
      reader.onerror = () => { setUploading(false); toast.error("File read failed"); };
      reader.readAsDataURL(file);
    } catch {
      setUploading(false);
    }
  };

  const removeCustomFont = async (f) => {
    if (!window.confirm(`Remove custom font "${f.family}"?`)) return;
    try {
      await api.delete(`/me/fonts/${f.font_id}`);
      await loadCustomFonts();
    } catch { toast.error("Remove failed"); }
  };

  // Key-Pair fonts.
  const kpFonts = useMemo(() => {
    return (pairs || [])
      .filter((p) => p.value_type === "font" && p.value && p.value.trim())
      .map((p) => ({ key: p.key, font: p.value.trim() }));
  }, [pairs]);

  const reposition = () => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ left: r.left, top: r.bottom + 4, width: Math.max(r.width, 220) });
  };

  useEffect(() => {
    if (!open) return;
    reposition();
    setTimeout(() => inputRef.current?.focus(), 30);
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = (e) => {
      if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      reposition();
    };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  const pick = (font) => {
    onChange(font);
    pushRecent(font);
    setRecents(loadRecents());
    setOpen(false);
    setQuery("");
  };

  // Filter — case-insensitive substring match.
  const matchesQuery = (font) => !query || font.toLowerCase().includes(query.toLowerCase());

  return (
    <div ref={wrapRef} className="relative inline-block w-32" data-testid={tid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-1.5 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-[11px] text-[#EDEDED] outline-none hover:border-[#5A90F5]"
        data-testid={`${tid}-trigger`}
      >
        <span className="truncate text-left flex-1" style={{ fontFamily: value || "Inter" }}>{value || "Inter"}</span>
        <ChevronDown className="w-3 h-3 text-[#A1A1AA] flex-shrink-0" />
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[300] bg-[#141414] border border-[#2A2A2A] rounded-md shadow-2xl py-1 max-h-80 flex flex-col"
          style={{ left: pos.left, top: pos.top, width: 240 }}
          data-themed-select-panel="1"
          data-testid={`${tid}-list`}
        >
          {/* Search */}
          <div className="px-2 py-1.5 border-b border-[#2A2A2A] sticky top-0 bg-[#141414]">
            <div className="flex items-center gap-1.5 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5">
              <Search className="w-3 h-3 text-[#A1A1AA]" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search fonts…"
                className="flex-1 bg-transparent border-0 outline-none text-[11px] py-1 text-[#EDEDED]"
                data-testid={`${tid}-search`}
              />
            </div>
            {/* Iter35 user request: custom font upload, Pro+ only. */}
            <div className="flex items-center gap-1 mt-1.5">
              <input
                ref={fileInputRef}
                type="file"
                accept=".woff,.woff2,.ttf,.otf,font/*"
                onChange={(e) => onUploadFile(e.target.files?.[0])}
                className="hidden"
                data-testid={`${tid}-upload-file`}
              />
              <button
                type="button"
                onClick={() => {
                  if (!canUploadCustomFonts) {
                    window.dispatchEvent(new CustomEvent("crescendo:paywall", {
                      detail: { error: "feature_locked", feature: "custom_fonts", plan: "pro", message: "Custom font uploads require Pro." },
                    }));
                    return;
                  }
                  fileInputRef.current?.click();
                }}
                disabled={uploading}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1 rounded border text-[10px] uppercase tracking-wider ${canUploadCustomFonts ? "border-[#5A90F5]/40 text-[#5A90F5] hover:bg-[#5A90F5]/10" : "border-[#2A2A2A] text-[#A1A1AA] hover:bg-[#1a1a1a]"} disabled:opacity-50`}
                data-testid={`${tid}-upload-btn`}
                title={canUploadCustomFonts ? "Upload custom font (.woff/.ttf/.otf)" : "Pro+ feature"}
              >
                {canUploadCustomFonts ? <Upload className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                {uploading ? "Uploading…" : "Upload Font"}
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {/* Custom Fonts (Pro+) — appear first if any */}
            {customFonts.length > 0 && customFonts.some((f) => matchesQuery(f.family)) && (
              <>
                <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-[#E0B355] flex items-center gap-1">
                  <Upload className="w-2.5 h-2.5" /> Custom Fonts
                </div>
                {customFonts.filter((f) => matchesQuery(f.family)).map((f) => (
                  <div key={f.font_id} className="group flex items-stretch hover:bg-[#1a1a1a]">
                    <button
                      type="button"
                      onClick={() => pick(f.family)}
                      className={`flex-1 text-left px-2.5 py-1.5 text-[11px] flex items-center gap-2 ${value === f.family ? "text-[#EDEDED]" : "text-[#A1A1AA]"}`}
                      style={{ fontFamily: f.family }}
                      data-testid={`${tid}-custom-${f.font_id}`}
                    >
                      <span className="flex-1 truncate">{f.family}</span>
                      {value === f.family && <Check className="w-3 h-3 text-[#5A90F5] flex-shrink-0" />}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeCustomFont(f); }}
                      className="px-2 opacity-0 group-hover:opacity-100 text-[#FF3B30]"
                      title="Remove font"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </>
            )}

            {/* Key-Pair Fonts */}
            {kpFonts.length > 0 && kpFonts.some((kp) => matchesQuery(kp.font)) && (
              <>
                <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-[#5A90F5] flex items-center gap-1">
                  <Key className="w-2.5 h-2.5" /> Key-Pair Fonts
                </div>
                {kpFonts.filter((kp) => matchesQuery(kp.font)).map((kp) => (
                  <button
                    key={`kp-${kp.key}`}
                    type="button"
                    onClick={() => pick(kp.font)}
                    className={`w-full text-left px-2.5 py-1.5 text-[11px] flex items-center gap-2 hover:bg-[#1a1a1a] ${value === kp.font ? "bg-[#1a1a1a] text-[#EDEDED]" : "text-[#A1A1AA]"}`}
                    style={{ fontFamily: kp.font }}
                    data-testid={`${tid}-kp-${kp.key}`}
                  >
                    <span className="flex-1 truncate">{kp.font}</span>
                    <span className="text-[8px] text-[#52525B] font-mono">@{kp.key}</span>
                    {value === kp.font && <Check className="w-3 h-3 text-[#5A90F5] flex-shrink-0" />}
                  </button>
                ))}
              </>
            )}

            {/* Recents */}
            {recents.length > 0 && recents.some(matchesQuery) && (
              <>
                <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-[#A1A1AA] mt-1">Recently Used</div>
                {recents.filter(matchesQuery).map((font) => (
                  <button
                    key={`r-${font}`}
                    type="button"
                    onClick={() => pick(font)}
                    className={`w-full text-left px-2.5 py-1.5 text-[11px] flex items-center gap-2 hover:bg-[#1a1a1a] ${value === font ? "bg-[#1a1a1a] text-[#EDEDED]" : "text-[#A1A1AA]"}`}
                    style={{ fontFamily: font }}
                    data-testid={`${tid}-recent-${font.replace(/\s+/g, "-")}`}
                  >
                    <span className="flex-1 truncate">{font}</span>
                    {value === font && <Check className="w-3 h-3 text-[#5A90F5] flex-shrink-0" />}
                  </button>
                ))}
              </>
            )}

            {/* All Fonts */}
            <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-[#A1A1AA] mt-1">All Fonts</div>
            {ALL_FONTS.filter(matchesQuery).map((font) => (
              <button
                key={`a-${font}`}
                type="button"
                onClick={() => pick(font)}
                className={`w-full text-left px-2.5 py-1.5 text-[11px] flex items-center gap-2 hover:bg-[#1a1a1a] ${value === font ? "bg-[#1a1a1a] text-[#EDEDED]" : "text-[#A1A1AA]"}`}
                style={{ fontFamily: font }}
                data-testid={`${tid}-option-${font.replace(/\s+/g, "-")}`}
              >
                <span className="flex-1 truncate">{font}</span>
                {value === font && <Check className="w-3 h-3 text-[#5A90F5] flex-shrink-0" />}
              </button>
            ))}
            {ALL_FONTS.filter(matchesQuery).length === 0 && kpFonts.filter((kp) => matchesQuery(kp.font)).length === 0 && (
              <div className="px-2.5 py-3 text-[10px] text-[#52525B] text-center">No fonts match "{query}"</div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
